// `/verify-reset-code` — ввод OTP-кода для сброса пароля.
// Source: docs/specs/auth-otp-verification (reset-флоу).
//
// Флоу: /forgot-password (email в PasswordResetContext) → этот экран → по вводу
// 6 цифр POST /auth/verify-reset-code → reset_token (broker-токен) → кладём в
// контекст → /auth/reset (форма нового пароля). НЕ логинит (логин — после смены
// пароля). Resend = повторный /auth/forgot-password (cooldown 60с).
//
// Дизайн — общий с /verify-code (палитра лендинга, зелёные ячейки).

import { useEffect, useState } from 'react';
import { Redirect, useLocation } from 'wouter';
import { flushSync } from 'react-dom';
import { Loader2, KeyRound } from 'lucide-react';
import { useForgotPasswordMutation, useVerifyResetCodeMutation } from '@/api/hooks/auth';
import { isApiError } from '@/api/error';
import { usePasswordResetContext } from '@/context/PasswordResetContext';
import { useToast } from '@/hooks/use-toast';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';

const RESEND_COOLDOWN_S = 60;
const CODE_LENGTH = 6;

const SLOT_CLASS =
  'h-14 w-11 rounded-surface border-primary/30 bg-background text-2xl font-bold text-foreground shadow-sm ' +
  'ring-primary ring-offset-2 transition-all first:rounded-surface last:rounded-surface ' +
  'data-[active=true]:border-primary';

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  if (local.length <= 2) return `${local[0]}***@${domain}`;
  return `${local[0]}***${local[local.length - 1]}@${domain}`;
}

export function VerifyResetCode() {
  const [, setLocation] = useLocation();
  const passwordReset = usePasswordResetContext();
  const verifyMutation = useVerifyResetCodeMutation();
  const forgotMutation = useForgotPasswordMutation();
  const { toast } = useToast();
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (!passwordReset.state) {
    return <Redirect to="/forgot-password" />;
  }

  const { email } = passwordReset.state;

  const handleComplete = async (value: string) => {
    if (verifyMutation.isPending) return;
    try {
      const { reset_token } = await verifyMutation.mutateAsync({ email, code: value });
      // reset_token → контекст → форма нового пароля (НЕ кладём в URL). flushSync —
      // контекст должен примениться до того, как /auth/reset прочитает его на mount'е.
      flushSync(() => {
        passwordReset.set({ email, resetToken: reset_token });
      });
      setLocation('/auth/reset');
    } catch (err) {
      setCode('');
      if (!isApiError(err)) {
        toast({ title: 'Ошибка', description: 'Попробуйте ещё раз', variant: 'destructive' });
        return;
      }
      const status = err.response?.status;
      if (status === 422) {
        toast({
          title: 'Неверный или истёкший код',
          description: 'Проверьте код из письма или запросите новый',
          variant: 'destructive',
        });
      } else if (status === 429) {
        toast({
          title: 'Слишком много попыток',
          description: 'Попробуйте через минуту',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Ошибка сервера', description: 'Попробуйте ещё раз', variant: 'destructive' });
      }
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || forgotMutation.isPending) return;
    try {
      await forgotMutation.mutateAsync({ email });
      toast({ title: 'Если код ещё актуален, мы отправили его повторно' });
      setCooldown(RESEND_COOLDOWN_S);
      setCode('');
    } catch (err) {
      if (isApiError(err) && err.response?.status === 429) {
        toast({
          title: 'Слишком много запросов',
          description: 'Попробуйте через минуту',
          variant: 'destructive',
        });
        setCooldown(RESEND_COOLDOWN_S);
      } else {
        toast({
          title: 'Не удалось отправить код',
          description: 'Попробуйте через минуту',
          variant: 'destructive',
        });
      }
    }
  };

  const resendDisabled = cooldown > 0 || forgotMutation.isPending;

  return (
    <MobileFrame>
      <ScreenHeader onBack={() => setLocation('/forgot-password')} title="Сброс пароля" />

      <div className="relative flex flex-1 flex-col overflow-hidden bg-gradient-to-b from-primary/5 to-transparent px-6 pb-6 pt-4">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

        <div className="relative flex flex-1 flex-col">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-surface bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-lg shadow-primary/25">
              <KeyRound className="h-8 w-8" strokeWidth={1.75} />
            </div>
            <h1 className="mb-1.5 text-hl-title font-bold leading-tight tracking-tight">
              Введите код из письма
            </h1>
            <p className="text-sm leading-snug text-muted-foreground">
              Мы отправили 6-значный код на
              <br />
              <span className="font-semibold text-foreground">{maskEmail(email)}</span>
            </p>
          </div>

          <div className="mb-3 flex justify-center">
            <InputOTP
              maxLength={CODE_LENGTH}
              value={code}
              onChange={setCode}
              onComplete={(v) => void handleComplete(v)}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d*"
              disabled={verifyMutation.isPending}
              containerClassName="justify-center gap-2 sm:gap-2.5"
            >
              {Array.from({ length: CODE_LENGTH }, (_, i) => (
                <InputOTPGroup key={i}>
                  <InputOTPSlot index={i} className={SLOT_CLASS} />
                </InputOTPGroup>
              ))}
            </InputOTP>
          </div>

          <div className="mb-7 flex h-5 items-center justify-center">
            {verifyMutation.isPending && (
              <span className="flex items-center gap-1.5 text-hl-secondary font-medium text-primary-text">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Проверяем код…
              </span>
            )}
          </div>

          <div className="text-center text-sm text-muted-foreground">
            Не пришёл код?{' '}
            <button
              type="button"
              onClick={() => void handleResend()}
              disabled={resendDisabled}
              className="font-semibold text-primary-text underline-offset-2 transition-colors hover:text-primary/80 hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
            >
              {cooldown > 0 ? `Отправить ещё раз (${cooldown}с)` : 'Отправить ещё раз'}
            </button>
          </div>

          <div className="mt-auto pt-8">
            <div className="rounded-surface border border-primary/20 bg-primary/5 p-3.5 text-hl-small leading-snug">
              <div className="mb-0.5 font-semibold text-foreground">Не нашли письмо?</div>
              <span className="text-muted-foreground">
                Проверьте папку «Спам». Код приходит с адреса{' '}
                <span className="font-mono text-foreground">noreply@healthlab.kz</span> и действует
                10 минут.
              </span>
            </div>
          </div>
        </div>
      </div>
    </MobileFrame>
  );
}
